import React, { useState } from 'react';
import Modal from './Modal';
import UnteraufgabenEditor from './UnteraufgabenEditor';
import { PRIORITAETEN } from './todoConstants';

/** Anfangszustand des Vorlagen-Formulars (leer oder aus bestehender Vorlage). */
const initialFormular = (vorlage) => ({
  name: vorlage?.name ?? '',
  beschreibung: vorlage?.beschreibung ?? '',
  prioritaet: vorlage?.prioritaet ?? 'MITTEL',
  kategorieId: vorlage?.kategorieId != null ? String(vorlage.kategorieId) : '',
  unteraufgaben: vorlage?.unteraufgaben?.map((u) => ({ titel: u.titel })) ?? [],
});

/**
 * Modal-Formular zum Anlegen/Bearbeiten einer wiederverwendbaren Vorlage.
 *
 * @param {{ vorlage?: object, kategorien: object[], onSubmit: (daten) => void, onClose: () => void }} props
 */
export default function VorlageForm({ vorlage, kategorien, onSubmit, onClose }) {
  const [formular, setFormular] = useState(() => initialFormular(vorlage));

  const setFeld = (feld, wert) => setFormular((f) => ({ ...f, [feld]: wert }));

  const speichern = (e) => {
    e.preventDefault();
    if (!formular.name.trim()) return;
    onSubmit({
      ...vorlage,
      name: formular.name.trim(),
      beschreibung: formular.beschreibung.trim() || null,
      prioritaet: formular.prioritaet,
      kategorieId: formular.kategorieId ? Number(formular.kategorieId) : null,
      unteraufgaben: formular.unteraufgaben.filter((u) => u.titel.trim()),
    });
  };

  return (
    <Modal titel={vorlage ? 'Vorlage bearbeiten' : 'Neue Vorlage'} onClose={onClose}>
      <form onSubmit={speichern} className="space-y-4">
        <Feld label="Name">
          <input
            type="text"
            value={formular.name}
            onChange={(e) => setFeld('name', e.target.value)}
            autoFocus
            className="w-full rounded-lg border border-line bg-paper-soft px-3 py-2 text-ink outline-none focus:border-brand-500"
          />
        </Feld>

        <Feld label="Beschreibung">
          <textarea
            value={formular.beschreibung}
            onChange={(e) => setFeld('beschreibung', e.target.value)}
            rows={2}
            className="w-full resize-y rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
          />
        </Feld>

        <div className="grid grid-cols-2 gap-4">
          <Feld label="Priorität">
            <select
              value={formular.prioritaet}
              onChange={(e) => setFeld('prioritaet', e.target.value)}
              className="w-full rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
            >
              {PRIORITAETEN.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </Feld>

          <Feld label="Kategorie">
            <select
              value={formular.kategorieId}
              onChange={(e) => setFeld('kategorieId', e.target.value)}
              className="w-full rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
            >
              <option value="">Keine</option>
              {kategorien.map((k) => (
                <option key={k.uuid} value={k.id}>{k.name}</option>
              ))}
            </select>
          </Feld>
        </div>

        <Feld label="Unteraufgaben">
          <UnteraufgabenEditor
            items={formular.unteraufgaben}
            onChange={(items) => setFeld('unteraufgaben', items)}
          />
        </Feld>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5">
            Abbrechen
          </button>
          <button type="submit" className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500">
            Speichern
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Beschriftetes Formularfeld. */
function Feld({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-xs uppercase tracking-wide text-ink-muted">{label}</span>
      {children}
    </label>
  );
}
